import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Heart, Loader2, AlertCircle, LogIn } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { EmailConfirmation } from './EmailConfirmation';
import { createLogger } from '../../lib/logger';

const log = createLogger('auth');

interface RegisterFormProps {
  onSignInClick?: () => void;
  onSuccess?: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSignInClick, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | JSX.Element>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = (): string | null => {
    if (!formData.email || !formData.password) {
      return '請填寫必填欄位';
    }

    if (formData.password.length < 6) {
      return '密碼至少需要 6 個字元';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return '請輸入有效的電子郵件格式';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      log.debug('RegisterForm: 開始註冊流程，email:', formData.email);

      log.debug('RegisterForm: 調用 signUp...');
      const { user, error: signUpError } = await signUp(formData.email, formData.password);
      
      log.debug('RegisterForm: signUp 結果:', {
        hasUser: !!user,
        hasError: !!signUpError,
        errorMessage: signUpError?.message
      });

      if (signUpError) {
        log.debug('RegisterForm: 註冊失敗，顯示錯誤');
        setError(getErrorMessage(signUpError));
      } else {
        if (user) {
          log.info('RegisterForm: 註冊成功，跳轉到 dashboard');
          if (onSuccess) {
            onSuccess();
          } else {
            navigate('/dashboard');
          }
        } else {
          log.debug('RegisterForm: 需要 email 確認');
          setShowEmailConfirmation(true);
        }
      }
    } catch (err) {
      log.error('RegisterForm: 捕獲異常:', err);
      setError('註冊時發生未知錯誤，請稍後再試');
    } finally {
      setIsLoading(false);
    }
  };

  // 處理 Google 登入建議
  const handleGoogleLoginSuggestion = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      
      if (error) {
        log.error('RegisterForm: Google 登入錯誤:', error);
        alert(`Google 登入失敗: ${error.message}`);
      }
    } catch (err) {
      log.error('RegisterForm: Google 登入未預期錯誤:', err);
      alert(`Google 登入錯誤: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorMessage = (error: any): JSX.Element | string => {
    // 調試日誌：記錄實際錯誤訊息
    log.debug('RegisterForm: 收到錯誤:', error?.message);
    
    // 檢測各種 email 衝突的錯誤訊息格式
    const isEmailConflict = error?.message?.includes('User already registered') || 
                          error?.message?.includes('Email already in use') ||
                          error?.message?.includes('Email already exists') ||
                          error?.message?.includes('already been registered');
    
    if (isEmailConflict) {
      return (
        <div className="space-y-4">
          <div className="flex items-center text-red-600">
            <AlertCircle className="w-4 h-4 mr-2" />
            <span>這個 email 已經註冊過了</span>
          </div>
          
          <div className="bg-wedding-action/5 border border-wedding-action/20 rounded-md p-4">
            <p className="text-wedding-text-title font-medium mb-3">您可以嘗試：</p>
            <div className="space-y-2">
              <button 
                type="button"
                onClick={() => navigate('/login')}
                className="w-full text-left px-3 py-2 bg-wedding-action/10 hover:bg-wedding-action/20 rounded-md text-wedding-action transition-colors flex items-center"
                disabled={isLoading}
              >
                <Mail className="w-4 h-4 mr-2" />
                使用 Email + 密碼登入
              </button>
              <button 
                type="button"
                onClick={handleGoogleLoginSuggestion}
                className="w-full text-left px-3 py-2 bg-wedding-action/10 hover:bg-wedding-action/20 rounded-md text-wedding-action transition-colors flex items-center"
                disabled={isLoading}
              >
                <LogIn className="w-4 h-4 mr-2" />
                使用 Google 登入
              </button>
            </div>
            <p className="text-xs text-wedding-text-body mt-3">
              💡 如果您忘記了註冊方式，建議先嘗試 Google 登入
            </p>
          </div>
        </div>
      );
    }
    if (error?.message?.includes('Password should be at least 6 characters')) {
      return '密碼至少需要 6 個字元';
    }
    if (error?.message?.includes('Invalid email')) {
      return '請輸入有效的電子郵件格式';
    }
    return error?.message || '註冊失敗，請稍後再試';
  };

  // 如果需要顯示 email 確認頁面
  if (showEmailConfirmation) {
    return (
      <EmailConfirmation
        email={formData.email}
        onBack={() => setShowEmailConfirmation(false)}
        onSuccess={() => {
          setShowEmailConfirmation(false);
          if (onSignInClick) {
            onSignInClick();
          } else {
            navigate('/login');
          }
        }}
      />
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-xl shadow-lg shadow-wedding-pink/20 p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-wedding-action/10 rounded-full mx-auto mb-4">
            <Heart className="w-8 h-8 text-wedding-action" fill="currentColor" fillOpacity={0.15} />
          </div>
          <h1 className="text-2xl font-bold text-wedding-text-title mb-2">開始做你們的邀請函</h1>
          <p className="text-wedding-text-body">免費建立，3 分鐘完成</p>
        </div>

        {/* Google 註冊按鈕 - 使用成功的配置 */}
        <div className="mt-2">
          <button
            onClick={async () => {
              log.debug('RegisterForm: Google 註冊 - 開始 OAuth 流程');
              try {
                const { error } = await supabase.auth.signInWithOAuth({
                  provider: 'google',
                  options: {
                    redirectTo: `${window.location.origin}/auth/callback`,
                    queryParams: {
                      access_type: 'offline',
                      prompt: 'consent',
                    },
                  },
                });
                
                if (error) {
                  log.error('RegisterForm: Google 註冊錯誤:', error);
                  alert(`Google 註冊失敗: ${error.message}`);
                } else {
                  log.info('RegisterForm: Google OAuth 初始化成功');
                }
              } catch (err) {
                log.error('RegisterForm: Google 註冊未預期錯誤:', err);
                alert(`Google 註冊錯誤: ${err}`);
              }
            }}
            className="w-full bg-white text-gray-700 border border-gray-300 px-4 py-3 rounded-md hover:bg-gray-50 font-medium"
          >
            <svg className="inline-block w-5 h-5 mr-2 align-text-bottom" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            使用 Google 註冊
          </button>
        </div>

        {/* 分隔線 */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">或</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              {typeof error === 'string' ? (
                <p className="text-red-600 text-sm">{error}</p>
              ) : (
                error
              )}
            </div>
          )}
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-wedding-text-title mb-2">
              電子郵件
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-wedding-action focus:border-wedding-action transition-colors"
                placeholder="輸入您的電子郵件"
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-wedding-text-title mb-2">
              密碼
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
                autoComplete="new-password"
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-wedding-action focus:border-wedding-action transition-colors"
                placeholder="至少 6 個字元"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isLoading}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-wedding-action text-white py-3 rounded-md hover:bg-wedding-action-hover focus:ring-2 focus:ring-wedding-action focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                註冊中...
              </>
            ) : (
              <>
                <Heart className="w-5 h-5 mr-2" />
                建立帳號
              </>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-wedding-text-body">
            已經有帳號了？{' '}
            <button
              onClick={() => onSignInClick ? onSignInClick() : navigate('/login')}
              className="text-wedding-action hover:text-wedding-action-hover font-medium transition-colors"
              disabled={isLoading}
            >
              立即登入
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};